/**
 * Temporary screen-scoped design tokens for **Settings → Security**.
 *
 * Mirrors the Settings design (`Settings.dc.html`, "Security" section) 1:1 —
 * every colour, size, radius, shadow, spacing and type step the four cards need
 * lives here so the components never carry a raw literal. These stay
 * screen-local on purpose: once every settings screen has been re-skinned the
 * shared values get promoted into the MUI theme and this file is deleted.
 */

import {
  button,
  contentCard,
  formField,
  inkfree,
  notification,
  signer,
  switchControl,
  white,
} from "theme/colors";

export const securityTokens = {
  /** White rounded card chrome shared by all four blocks on the screen. */
  card: {
    bg: white,
    border: contentCard.borderMuted,
    borderWidth: "1px",
    radius: "14px",
    shadow: "0 1px 3px rgba(33,21,71,.04)",
    /** Padding of the fully padded cards (Change Password, MFA). */
    padding: "24px 28px",
    /** Header padding of the split cards (Passkeys, Device activity). */
    headerPadding: "20px 28px 16px",
    /** Gap between a split-card header's text block and its action. */
    headerGap: "14px",
    /** Gap between the blocks stacked inside the Change Password card. */
    bodyGap: "16px",
  },
  /** Card heading + supporting sentence. */
  heading: {
    color: signer.ink,
    fontSize: "15px",
    fontWeight: 800,
    letterSpacing: "-0.01em",
    lineHeight: "normal",
    /** Change Password stacks title/description 3px apart… */
    gap: "3px",
    /** …the split cards use 2px. */
    tightGap: "2px",
  },
  description: {
    color: signer.text.meta,
    fontSize: "12.5px",
    lineHeight: 1.55,
  },
  field: {
    /*
      Field geometry is the app's, not this screen's: 44px on a 10px radius
      with 14px text and a 14px inset, matching Contacts and the shared
      `CustomInput` default. These four Settings screens had drifted to a
      40/9px/13px box of their own, which read as a different control from one
      page to the next. See `global/components/CustomInput/FormField.tokens.ts`.
    */
    rowGap: "14px",
    columnGap: "18px",
    labelGap: "6px",
    labelFontSize: "11.5px",
    labelFontWeight: 600,
    labelColor: signer.ink,
    requiredColor: signer.orange.main,
    height: 44,
    radius: "10px",
    border: formField.border,
    bg: formField.bg,
    color: signer.ink,
    fontSize: "14px",
    fontWeight: 500,
    /*
      15px, not 14: the design inset is measured inside the field's 1px border,
      but MUI paints that border on an overlaid `fieldset` rather than
      insetting it, so the input needs design padding + border width to land on
      the same pixel. Contacts resolves to the same 15px.
    */
    paddingX: "15px",
    focusBorder: signer.orange.main,
    placeholder: formField.placeholderMuted,
    errorFontSize: "10.5px",
  },
  /** Cream panel listing the four password requirements. */
  requirements: {
    bg: "#FAF8F3",
    border: "#EFEBE0",
    radius: "11px",
    padding: "14px 18px",
    rowGap: "8px",
    columnGap: "24px",
    fontSize: "12px",
    color: signer.text.meta,
    gap: "8px",
    dotSize: 6,
    dotColor: "#D8D2C2",
    /** Requirement already satisfied — the design only shows the idle state. */
    metDotColor: signer.orange.main,
    metColor: signer.ink,
  },
  /** "Sign out other devices" / "Stay signed in everywhere" radio cards. */
  sessionChoice: {
    groupGap: "8px",
    optionGap: "12px",
    padding: "12px 14px",
    radius: "11px",
    borderWidth: "1.5px",
    border: contentCard.borderMuted,
    borderSelected: signer.orange.main,
    bg: white,
    bgSelected: signer.warn.tint,
    contentGap: "10px",
    textGap: "2px",
    radioSize: 16,
    radioBorderWidth: "1.5px",
    radioBorder: switchControl.trackOff,
    radioBorderSelected: signer.orange.main,
    radioDotSize: 8,
    radioDot: signer.orange.main,
    radioOffsetTop: "1px",
    titleFontSize: "12.5px",
    titleFontWeight: 700,
    titleColor: signer.ink,
    descFontSize: "11.5px",
    descColor: signer.text.meta,
    descLineHeight: 1.5,
  },
  /**
   * Filled brand action ("Update Password", "Create passkey"). Chrome mirrors
   * the global `MuiButton` `contained` override — see `button`.
   */
  primaryButton: {
    ...button,
    /** Compact variant used inside the card headers. */
    compactHeight: 34,
    compactPaddingX: "18px",
    compactFontSize: "12.5px",
    spinnerSize: 15,
    spinnerThickness: 5,
    spinnerGap: "8px",
  },
  /** Outlined action sitting on the right of a card header ("Set Up"). */
  outlinedButton: {
    height: 38,
    paddingX: "18px",
    radius: "9px",
    bg: white,
    border: signer.orange.main,
    color: signer.orange.main,
    hoverBg: signer.warn.tint,
    fontSize: "12.5px",
    fontWeight: 700,
    /** Neutral variant ("View QR Code"). */
    neutralBorder: formField.border,
    neutralColor: signer.ink,
    neutralHoverBg: signer.surface.muted,
    /** Destructive variant ("Disable"). */
    dangerBorder: "#E8C9C9",
    dangerColor: notification.error.icon,
    dangerHoverBg: signer.danger.tint,
    dangerHoverBorder: notification.error.icon,
    gap: "10px",
  },
  /** Square tile holding the card's leading glyph (MFA). */
  leadingIcon: {
    size: 44,
    radius: "11px",
    bg: signer.surface.muted,
    iconSize: 20,
    iconColor: signer.ink,
    iconStrokeWidth: 2,
    gap: "16px",
  },
  /** Status pill next to a card title, and the "THIS DEVICE" row badge. */
  pill: {
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: "0.05em",
    padding: "3px 9px",
    radius: "999px",
    dangerColor: notification.error.icon,
    dangerBg: notification.error.bg,
    successColor: notification.success.icon,
    successBg: notification.success.bg,
    /** Title ↔ pill gap. */
    gap: "10px",
    /** In-row badge is tighter than the header pill and is not tracked out. */
    rowPadding: "2px 8px",
    rowLetterSpacing: "normal",
    rowMarginLeft: "6px",
  },
  /** Grey column strip + rows shared by the passkey and session tables. */
  table: {
    gutter: "28px",
    headerPaddingY: "10px",
    headerBg: signer.surface.subtle,
    headerBorder: signer.border.light,
    headerFontSize: "10.5px",
    headerFontWeight: 700,
    headerLetterSpacing: "0.09em",
    headerColor: signer.text.faint,
    rowPaddingY: "14px",
    rowDivider: signer.border.divider,
    rowFontSize: "13px",
    rowColor: signer.ink,
    mutedColor: signer.text.meta,
    deviceGap: "10px",
    deviceFontWeight: 600,
    deviceIconSize: 16,
    deviceIconColor: signer.ink,
    deviceIconStrokeWidth: 2,
    actionFontSize: "12px",
    actionFontWeight: 700,
    actionColor: signer.orange.main,
    actionHoverColor: signer.orange.dark,
    /** Grid templates copied verbatim from the design. */
    sessionColumns: "1fr 200px 140px 60px",
    passkeyColumns: "1fr 200px 60px",
    skeletonRows: 3,
    skeletonHeight: 16,
  },
  /** Centred "No passkeys yet" block under the Passkeys header. */
  emptyState: {
    paddingY: "36px",
    gap: "8px",
    circleSize: 48,
    circleBg: signer.surface.muted,
    iconSize: 20,
    iconColor: signer.text.faint,
    iconStrokeWidth: 2,
    titleFontSize: "13px",
    titleFontWeight: 700,
    titleColor: signer.ink,
    descFontSize: "12px",
    descColor: signer.text.faint,
    /** Row-table empty message (sessions). */
    rowPaddingY: "34px",
    rowFontSize: "12.5px",
    rowColor: signer.text.meta,
  },
  /** Right-aligned pager closing a table that spills past one page. */
  pagination: {
    paddingY: "14px",
    border: signer.border.light,
    gap: "4px",
    itemSize: "28px",
    itemRadius: "7px",
    itemFontSize: "12px",
    itemFontWeight: 600,
    itemColor: signer.text.meta,
    itemHoverBg: signer.surface.muted,
    selectedBg: inkfree.main,
    selectedColor: white,
    disabledColor: switchControl.trackOff,
    arrowSize: "11px",
    arrowStrokeWidth: 2.5,
  },
  /** Row action icon buttons (rename / delete a passkey). */
  rowAction: {
    gap: "4px",
    size: 28,
    radius: "7px",
    iconSize: 15,
    iconStrokeWidth: 2,
    color: signer.text.meta,
    hoverBg: signer.surface.muted,
    hoverColor: signer.ink,
    dangerHoverColor: notification.error.icon,
  },
} as const;

/**
 * Phone (`xs`) and tablet (`sm`) values for **Settings → Security**, traced from
 * `Settings - Mobile & Tablet.dc.html`.
 *
 * Only what differs from the desktop tokens above lives here; anything omitted
 * is inherited at `md`, so the desktop screen is untouched. `sm` / `std` inside
 * `phone` are the reference's 375 and 390 columns.
 */
export const securityResponsiveTokens = {
  phone: {
    card: {
      padding: { sm: "18px 16px", std: "20px 18px" },
      bodyGap: { sm: "14px", std: "15px" },
      /** Split cards (Passkeys, Device activity) stack header text over action. */
      headerPadding: { sm: "18px 16px 14px", std: "20px 18px 15px" },
      headerGap: { sm: "10px", std: "11px" },
    },
    heading: { fontSize: { sm: "14.5px", std: "15px" } },
    description: { fontSize: { sm: "12px", std: "12.5px" } },
    field: {
      labelFontSize: { sm: "11.5px", std: "12px" },
      height: { sm: 42, std: 44 },
      fontSize: { sm: "13.5px", std: "14px" },
    },
    requirements: {
      padding: { sm: "12px 15px", std: "13px 16px" },
      rowGap: "7px",
      fontSize: { sm: "11.5px", std: "12px" },
    },
    primaryButton: {
      height: { sm: 42, std: 44 },
      paddingX: { sm: "20px", std: "22px" },
      fontSize: { sm: "13px", std: "13.5px" },
    },
    compactButton: {
      height: { sm: 38, std: 40 },
      paddingX: { sm: "16px", std: "18px" },
      fontSize: { sm: "12.5px", std: "13px" },
    },
    pill: {
      fontSize: { sm: "9.5px", std: "10px" },
      padding: { sm: "3px 8px", std: "3px 9px" },
    },
    /** Table gutters and the stacked-row rhythm the grid falls back to. */
    table: {
      gutter: { sm: "16px", std: "18px" },
      rowGap: "4px",
      rowPaddingY: "12px",
    },
  },

  tablet: {
    /** The reference stands Change Password beside the MFA/Passkeys column. */
    columnGap: "16px",
    card: {
      padding: "20px 22px",
      compactPadding: "18px 20px",
      bodyGap: "13px",
      headerPadding: "18px 20px 14px",
      headerGap: "10px",
    },
    heading: { fontSize: "14.5px", compactFontSize: "14px" },
    description: { fontSize: "11.5px" },
    field: {
      rowGap: "13px",
      labelFontSize: "11px",
      height: 38,
      radius: "8px",
      paddingX: "11px",
      fontSize: "13px",
    },
    requirements: {
      padding: "12px 14px",
      rowGap: "7px",
      fontSize: "11.5px",
    },
    primaryButton: {
      height: 38,
      paddingX: "18px",
      radius: "8px",
      fontSize: "12.5px",
    },
    compactButton: {
      height: 34,
      paddingX: "14px",
      radius: "8px",
      fontSize: "12px",
    },
    pill: { fontSize: "9.5px", padding: "3px 8px" },
    table: { gutter: "20px", rowGap: "4px", rowPaddingY: "12px" },
  },
} as const;

export default securityTokens;
